import { useCallback } from 'react';
import {
    useDataTableActionService,
    useFormDataTableActionWrapper,
} from '../../../../services/FormDataTableActionService';
import { resolveErrorHandler } from '../InfrastructureUtils';
import { useInfrastructureDeleteError } from './UseInfrastructureDeleteError';

/**
 * Custom hook for handling infrastructure detail page actions (Enable, Disable, Delete)
 * Provides centralized action handling with automatic error handling and state management
 *
 * @param {Object} config - Configuration object
 * @param {string} config.recordId - Current record ID to perform action on
 * @param {boolean} config.tableSelectionAction - use ids or selection as action post body
 * @param {Object} config.actionUrls - Map of action names to API endpoint URLs
 * @param {string} config.goToUrl - URL to navigate to after successful action
 * @param {Function} config.setIsActioning - State setter to track action in progress
 * @returns {Object} Action handlers and state
 * @returns {boolean} returns.dataTableActioning - Whether an action is currently in progress
 * @returns {Function} returns.handleConfirmedDataTableAction - Handler for confirmed actions
 *
 * @example
 * const { dataTableActioning, handleConfirmedDataTableAction } = useInfrastructureDetailActions({
 *   recordId: '12345',
 *   tableSelectionAction: true,
 *   actionUrls: { Delete: '/api/delete', Enable: '/api/enable' },
 *   goToUrl: '/infrastructure/list',
 *   setIsActioning: setActioningState
 * });
 */
export function useInfrastructureDetailActions({
    recordId,
    tableSelectionAction,
    actionUrls,
    goToUrl,
    setIsActioning,
}) {
    const formDataTableActionService = useDataTableActionService();
    const handleDeleteError = useInfrastructureDeleteError();
    const { dataTableActioning, dataTableActionWrapper } = useFormDataTableActionWrapper();

    /**
     * Handle confirmed data table action for detail page
     * @param {Event} event - Click event from action button
     * @param {string} action - Action name (ENABLE, DISABLE, DELETE)
     * @param {boolean} needAlert - Whether to show success alert after action completes
     */
    const handleConfirmedDataTableAction = useCallback(
        (event, action, needAlert = false) => {
            // Remove focus from button to prevent keyboard re-trigger
            event?.currentTarget.blur();

            const actionUrl = actionUrls[action];
            const errorHandler = resolveErrorHandler(actionUrl, handleDeleteError);

            setIsActioning(true);

            const actionPromise = dataTableActionWrapper({
                dataTableActionFunction: () =>
                    formDataTableActionService.dataTableActionDetailSelection(
                        action,
                        actionUrl,
                        {},
                        recordId,
                        tableSelectionAction,
                        errorHandler
                    ),
                onDataTableActionGoTo: goToUrl,
                action,
                needAlert,
            })();

            actionPromise.finally(() => {
                setIsActioning(false);
            });
        },
        [
            actionUrls,
            dataTableActionWrapper,
            formDataTableActionService,
            goToUrl,
            handleDeleteError,
            recordId,
            setIsActioning,
            tableSelectionAction,
        ]
    );

    return {
        dataTableActioning,
        handleConfirmedDataTableAction,
    };
}
